import * as React from 'react';

import { ColorScheme, ThemeDensity } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import {
  $localization,
  $theme,
  useSystemColorScheme,
} from '~/shared/state/preferences.state';
import { $userLanguageSetting } from '~/shared/state/settings.state';

export type PreferencesProviderProps = {
  children: React.ReactNode;
};

export type PreferencesContextType = {
  localization: {
    language: string;
    defaultCountry: string;
    timezone: string;
  };

  theme: {
    isDarkMode: boolean;
    colorScheme: ColorScheme | undefined;
    toggleTheme: () => void;
    density: ThemeDensity;
    setDensity: (density: ThemeDensity) => void;
  };
};

export const PreferencesContext = React.createContext<PreferencesContextType>({
  localization: {
    language: 'en',
    defaultCountry: 'US',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  },

  theme: {
    isDarkMode: false,
    colorScheme: undefined,
    toggleTheme: () => null,
    density: 'default',
    setDensity: () => null,
  },
});

export const PreferencesProvider = ({ children }: PreferencesProviderProps) => {
  const localization = useAtomValue($localization);
  const [theme, setTheme] = useAtom($theme);
  const systemColorScheme = useSystemColorScheme();
  const userLanguageSetting = useAtomValue($userLanguageSetting);
  const setLocalization = useSetAtom($localization);

  React.useEffect(() => {
    if (userLanguageSetting) {
      setLocalization({ language: userLanguageSetting });
    }
  }, [userLanguageSetting, setLocalization]);

  const toggleTheme = React.useCallback(() => {
    setTheme({
      colorScheme: theme.colorScheme === 'dark' ? 'light' : 'dark',
    });
  }, [setTheme, theme.colorScheme]);

  const setDensity = React.useCallback(
    (density: ThemeDensity) => {
      setTheme({
        density,
      });
    },
    [setTheme],
  );

  // Update density on document element
  React.useEffect(() => {
    document.documentElement.dataset.density = theme.density;
  }, [theme.density]);

  // Update theme on document body
  React.useEffect(() => {
    const isDark = theme.colorScheme
      ? theme.colorScheme === 'dark'
      : systemColorScheme === 'dark';

    document.body.classList.toggle('dark', isDark);

    // Update color-scheme meta tag
    const effectiveColorScheme = theme.colorScheme || systemColorScheme;
    document
      .querySelector('meta[name="color-scheme"]')
      ?.setAttribute('content', effectiveColorScheme);
  }, [theme.colorScheme, systemColorScheme]);

  const isDarkMode = theme.colorScheme
    ? theme.colorScheme === 'dark'
    : systemColorScheme === 'dark';

  return (
    <PreferencesContext.Provider
      value={{
        localization: {
          language: localization.language,
          defaultCountry: localization.defaultCountry,
          timezone: localization.timezone,
        },

        theme: {
          isDarkMode,
          colorScheme: theme.colorScheme,
          toggleTheme,
          density: theme.density,
          setDensity,
        },
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};
